import React, { useCallback } from 'react';
import { TextInput, StyleSheet, Pressable, Platform, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { XStack } from '@tamagui/stacks';
import { Send, Gift } from 'lucide-react-native';
import { COLORS } from '@/constants/colors';

type ChatInputProps = {
  value: string;
  onChangeText: (text: string) => void;
  onSend: () => void;
  onGiftPress: () => void;
  isDark?: boolean;
  disabled?: boolean;
};

const ChatInput = React.memo<ChatInputProps>(({ value, onChangeText, onSend, onGiftPress, isDark, disabled }) => {
  const bgColor = isDark ? COLORS.ZINC_900 : '#F0F0F0';
  const textColor = isDark ? COLORS.WHITE : COLORS.DARK_TEXT;
  const placeholderColor = isDark ? COLORS.GRAY_400 : COLORS.GRAY_500;
  const iconColor = COLORS.PINK;
  const sendColor = value.trim() ? COLORS.PINK : iconColor;

  // Handle Enter key press - send on Enter, new line on Shift+Enter
  const handleKeyPress = useCallback((e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (Platform.OS === 'web') {
      const nativeEvent = e.nativeEvent as any;
      if (nativeEvent.key === 'Enter' && !nativeEvent.shiftKey) {
        e.preventDefault();
        if (value.trim() && !disabled) {
          onSend();
        }
      }
    }
  }, [value, disabled, onSend]);

  return (
    <XStack
      alignItems="center"
      gap="$2"
      paddingHorizontal="$4"
      paddingVertical="$3"
      backgroundColor={isDark ? COLORS.BLACK : COLORS.WHITE}
      style={styles.container}
    >
      {/* Input Field */}
      <XStack
        flex={1}
        alignItems="center"
        paddingHorizontal="$3"
        paddingVertical="$2"
        borderRadius={20}
        backgroundColor={bgColor}
      >
        <TextInput
          value={value}
          onChangeText={onChangeText}
          onKeyPress={handleKeyPress}
          placeholder="Twoja wiadomość..."
          placeholderTextColor={placeholderColor}
          style={[styles.input, { color: textColor }]}
          multiline
          maxLength={500}
        />
      </XStack>

      {/* Gift Button */}
      <Pressable onPress={onGiftPress} style={[styles.iconButton, { backgroundColor: bgColor }]}>
        <Gift size={24} color={iconColor} />
      </Pressable>

      {/* Send Button */}
      <Pressable onPress={onSend} disabled={!value.trim() || disabled} style={[styles.iconButton, { backgroundColor: bgColor, opacity: disabled ? 0.5 : 1 }]}>
        <Send size={24} color={sendColor} fill={value.trim() ? sendColor : 'transparent'} />
      </Pressable>
    </XStack>
  );
});

const styles = StyleSheet.create({
  container: {
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  input: {
    flex: 1,
    fontSize: 16,
    maxHeight: 100,
    padding: 0,
  },
  iconButton: {
    padding: 8,
    borderRadius: 20,
  },
});

ChatInput.displayName = 'ChatInput';

export default ChatInput;


